import React, { Component } from 'react';
import axios from 'axios';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import {
  Button,
  ButtonToolbar,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { countryList } from '../../../../../helpers/shipping/CountriesNew';

const URL = 'https://api.musicmarquee.co.uk';
const authHeaders = () => ({ Authorization: localStorage.getItem('mm_admin') });
const currencyFormatGbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

const formatDate = value => (value ? `${value}`.slice(0, 10) : '');

const formatDateTimeInput = (value) => {
  if (!value) {
    return '';
  }

  return `${value}`.slice(0, 16);
};

const formatCsvValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return `"${`${value}`.replace(/"/g, '""')}"`;
};

const getCountryLabel = (countryCode) => {
  const country = countryList.find(item => item.value === countryCode);
  return country ? country.label : countryCode;
};

const parseOrderItems = (orderItems) => {
  if (!orderItems) {
    return [];
  }

  if (Array.isArray(orderItems)) {
    return orderItems;
  }

  try {
    return JSON.parse(orderItems);
  } catch (error) {
    return [];
  }
};

const editableFields = [
  { key: 'status', label: 'Status', type: 'text' },
  { key: 'shipping_service', label: 'Shipping Service', type: 'text' },
  { key: 'shipping_carrier', label: 'Shipping Carrier', type: 'text' },
  { key: 'tracking_number', label: 'Tracking Number', type: 'text' },
  { key: 'shipped_date', label: 'Shipped Date', type: 'datetime-local' },
  { key: 'actual_weight', label: 'Actual Weight', type: 'number' },
  {
    key: 'actual_shipping_price', label: 'Client Shipping Price', type: 'number', step: '0.01',
  },
  {
    key: 'mm_cost', label: 'MM Shipping Cost', type: 'number', step: '0.01',
  },
  { key: 'mm_package_size', label: 'MM Package Size', type: 'text' },
  {
    key: 'order_fee', label: 'Order Fee', type: 'number', step: '0.01',
  },
  {
    key: 'packaging_fee', label: 'Packaging Fee', type: 'number', step: '0.01',
  },
  {
    key: 'order_items_fee', label: 'Order Items Fee', type: 'number', step: '0.01',
  },
  {
    key: 'customs_tariff', label: 'Customs Tariff', type: 'number', step: '0.01',
  },
];

const getFieldValue = (fulfilment, field) => {
  if (!fulfilment || fulfilment[field.key] === null || fulfilment[field.key] === undefined) {
    return '';
  }

  if (field.type === 'datetime-local') {
    return formatDateTimeInput(fulfilment[field.key]);
  }

  return fulfilment[field.key];
};

const renderCenteredCheckbox = checkbox => (
  <div style={{ textAlign: 'center', width: '100%' }}>
    {checkbox}
  </div>
);

class DiscogsFulfilmentsTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      pages: 0,
      count: 0,
      page: 0,
      pageSize: 50,
      loading: false,
      exporting: false,
      selected: null,
      selectedOriginal: null,
      isEditing: false,
      deleteEnabled: false,
      sellers: [],
      sellerId: '',
      shippedDateFrom: '',
      shippedDateTo: '',
      shippedOnly: false,
      selectedIds: [],
      batchDeleteEnabled: false,
      saveMessage: '',
      deleteMessage: '',
      tableState: null,
      tableFilters: [],
    };
  }

  componentDidMount() {
    this.getSellers();
  }

  getSellers = () => {
    axios.get(
      `${URL}/admin/fulfillment/sellers`,
      { headers: authHeaders() },
    )
      .then((response) => {
        this.setState({
          sellers: response.data.sellers || [],
        });
      })
      .catch(() => {
        this.setState({
          saveMessage: 'There was a problem loading sellers.',
        });
      });
  };

  fetchData = (state) => {
    this.setState({
      loading: true,
      tableState: state,
      page: state.page,
      pageSize: state.pageSize,
    });

    axios.post(
      `${URL}/admin/fulfillment/fulfilments/list`,
      {
        page: state.page,
        pageSize: state.pageSize,
        sorted: state.sorted,
        filtered: state.filtered,
        sellerId: this.state.sellerId,
        shippedDateFrom: this.state.shippedDateFrom,
        shippedDateTo: this.state.shippedDateTo,
        shippedOnly: this.state.shippedOnly,
      },
      { headers: authHeaders() },
    )
      .then((response) => {
        this.setState({
          data: response.data.items || [],
          pages: response.data.pages || 0,
          count: response.data.count || 0,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({
          loading: false,
          saveMessage: 'There was a problem loading fulfilments.',
        });
      });
  };

  refreshTable = () => {
    if (this.state.tableState) {
      this.fetchData(Object.assign({}, this.state.tableState, {
        page: this.state.page,
        pageSize: this.state.pageSize,
        filtered: this.state.tableFilters,
      }));
    }
  };

  selectFulfilment = (fulfilment) => {
    this.setState({
      selected: Object.assign({}, fulfilment),
      selectedOriginal: Object.assign({}, fulfilment),
      isEditing: false,
      deleteEnabled: false,
      saveMessage: '',
      deleteMessage: '',
    });
  };

  updateFilter = (field, value) => {
    this.setState({ [field]: value, page: 0 }, () => {
      if (this.state.tableState) {
        this.fetchData(Object.assign({}, this.state.tableState, {
          page: 0,
          pageSize: this.state.pageSize,
          filtered: this.state.tableFilters,
        }));
      }
    });
  };

  updateTableFilters = (tableFilters) => {
    this.setState({ tableFilters, page: 0 });
  };

  resetFilters = () => {
    this.setState({
      sellerId: '',
      shippedDateFrom: '',
      shippedDateTo: '',
      shippedOnly: false,
      selectedIds: [],
      batchDeleteEnabled: false,
      tableFilters: [],
      page: 0,
    }, () => {
      if (this.state.tableState) {
        this.fetchData(Object.assign({}, this.state.tableState, {
          page: 0,
          pageSize: this.state.pageSize,
          filtered: [],
        }));
      }
    });
  };

  updateSelectedField = (field, value) => {
    this.setState(prevState => ({
      selected: Object.assign({}, prevState.selected, { [field]: value }),
    }));
  };

  cancelEdit = () => {
    this.setState(prevState => ({
      selected: Object.assign({}, prevState.selectedOriginal),
      isEditing: false,
      saveMessage: '',
    }));
  };

  saveFulfilment = () => {
    axios.post(
      `${URL}/admin/fulfillment/fulfilments/update`,
      {
        fulfilmentOrder: this.state.selected,
      },
      { headers: authHeaders() },
    )
      .then((response) => {
        const { fulfilmentOrder } = response.data;
        this.setState(prevState => ({
          selected: Object.assign({}, fulfilmentOrder),
          selectedOriginal: Object.assign({}, fulfilmentOrder),
          isEditing: false,
          saveMessage: 'Fulfilment updated.',
          data: prevState.data.map(row => (row.id === fulfilmentOrder.id ? fulfilmentOrder : row)),
        }));
      })
      .catch(() => {
        this.setState({ saveMessage: 'There was a problem saving this fulfilment.' });
      });
  };

  toggleSelectedId = (id, checked) => {
    this.setState((prevState) => {
      const selectedIds = checked ?
        prevState.selectedIds.concat([id]).filter((value, index, values) => values.indexOf(value) === index) :
        prevState.selectedIds.filter(selectedId => selectedId !== id);

      return {
        selectedIds,
        batchDeleteEnabled: selectedIds.length > 0 ? prevState.batchDeleteEnabled : false,
      };
    });
  };

  toggleVisibleSelections = (checked) => {
    this.setState((prevState) => {
      const visibleIds = prevState.data.map(row => row.id);
      const selectedIds = checked ?
        prevState.selectedIds.concat(visibleIds).filter((value, index, values) => values.indexOf(value) === index) :
        prevState.selectedIds.filter(id => !visibleIds.includes(id));

      return {
        selectedIds,
        batchDeleteEnabled: selectedIds.length > 0 ? prevState.batchDeleteEnabled : false,
      };
    });
  };

  deleteFulfilment = () => {
    if (!this.state.selected || !this.state.deleteEnabled) {
      return;
    }

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Delete fulfilment ${this.state.selected.id}? This cannot be undone.`);

    if (!confirmed) {
      return;
    }

    axios.post(
      `${URL}/admin/fulfillment/fulfilments/delete`,
      {
        id: this.state.selected.id,
      },
      { headers: authHeaders() },
    )
      .then(() => {
        this.setState({
          selected: null,
          selectedOriginal: null,
          deleteEnabled: false,
          deleteMessage: 'Fulfilment deleted.',
        }, this.refreshTable);
      })
      .catch(() => {
        this.setState({ deleteMessage: 'There was a problem deleting this fulfilment.' });
      });
  };

  batchDeleteFulfilments = () => {
    if (this.state.selectedIds.length === 0 || !this.state.batchDeleteEnabled) {
      return;
    }

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Delete ${this.state.selectedIds.length} fulfilments? This cannot be undone.`);

    if (!confirmed) {
      return;
    }

    axios.post(
      `${URL}/admin/fulfillment/fulfilments/delete`,
      {
        ids: this.state.selectedIds,
      },
      { headers: authHeaders() },
    )
      .then(() => {
        this.setState({
          selected: null,
          selectedOriginal: null,
          selectedIds: [],
          batchDeleteEnabled: false,
          deleteEnabled: false,
          deleteMessage: 'Selected fulfilments deleted.',
        }, this.refreshTable);
      })
      .catch(() => {
        this.setState({ deleteMessage: 'There was a problem deleting the selected fulfilments.' });
      });
  };

  exportCsv = () => {
    if (!this.state.tableState) {
      return;
    }

    this.setState({ exporting: true });

    axios.post(
      `${URL}/admin/fulfillment/fulfilments/list`,
      {
        page: 0,
        pageSize: this.state.count || 100000,
        sorted: this.state.tableState.sorted,
        filtered: this.state.tableFilters,
        sellerId: this.state.sellerId,
        shippedDateFrom: this.state.shippedDateFrom,
        shippedDateTo: this.state.shippedDateTo,
        shippedOnly: this.state.shippedOnly,
      },
      { headers: authHeaders() },
    )
      .then((response) => {
        const rows = response.data.items || [];
        const columns = [
          { label: 'MF ID', key: 'id' },
          { label: 'External Ref', key: 'external_order_id' },
          { label: 'Seller', key: 'seller_name' },
          { label: 'Customer Name', key: 'customer_name' },
          { label: 'Country', key: 'destination_country_code', format: getCountryLabel },
          { label: 'Shipped Date', key: 'shipped_date', format: formatDate },
          { label: 'Carrier', key: 'shipping_carrier' },
          { label: 'Tracking Number', key: 'tracking_number' },
          { label: 'Weight', key: 'actual_weight' },
          { label: 'Order Fee', key: 'order_fee' },
          { label: 'Packaging Fee', key: 'packaging_fee' },
          { label: 'Shipping Fee', key: 'actual_shipping_price' },
        ];
        const csvRows = [
          columns.map(column => formatCsvValue(column.label)).join(','),
          ...rows.map(row => columns
            .map((column) => {
              const value = column.format ? column.format(row[column.key]) : row[column.key];
              return formatCsvValue(value);
            })
            .join(',')),
        ];
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const date = new Date().toISOString().slice(0, 10);

        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `discogs-fulfilments-${date}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.setState({ exporting: false });
      })
      .catch(() => {
        this.setState({
          exporting: false,
          saveMessage: 'There was a problem exporting fulfilments.',
        });
      });
  };

  renderOrderItems = () => {
    if (!this.state.selected) {
      return null;
    }

    const orderItems = parseOrderItems(this.state.selected.order_items);

    return (
      <Card>
        <CardBody>
          <h4>Order Items</h4>
          {orderItems.length === 0 ? (
            <p>No order items found for this fulfilment.</p>
          ) : (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Condition</th>
                  <th>Sleeve Condition</th>
                  <th>Location</th>
                  <th>External ID</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map(item => (
                  <tr key={`${item.id || item.release_id || item.external_id}-${item.description || item.title}`}>
                    <td>{item.description || item.title || '-'}</td>
                    <td>{item.media_cond || item.media_condition || '-'}</td>
                    <td>{item.sleeve_cond || item.sleeve_condition || '-'}</td>
                    <td>{item.location || '-'}</td>
                    <td>{item.external_id || '-'}</td>
                    <td>
                      {item.price !== null && item.price !== undefined
                        ? currencyFormatGbp.format(item.price)
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    );
  };

  renderSelectedDetails = () => {
    if (!this.state.selected) {
      return (
        <Card>
          <CardBody>
            <p>Select a fulfilment to view or edit it.</p>
          </CardBody>
        </Card>
      );
    }

    return (
      <Card>
        <CardBody>
          <Row>
            <Col md={3}>
              <p><strong>ID:</strong> {this.state.selected.id}</p>
              <p><strong>Order:</strong> {this.state.selected.external_order_id}</p>
            </Col>
            <Col md={3}>
              <p><strong>Seller:</strong> {this.state.selected.seller_name || '-'}</p>
              <p><strong>Customer:</strong> {this.state.selected.customer_name || '-'}</p>
            </Col>
            <Col md={3}>
              <p><strong>Email:</strong> {this.state.selected.email || '-'}</p>
              <p><strong>Phone:</strong> {this.state.selected.phone || '-'}</p>
            </Col>
            <Col md={3}>
              <p><strong>Country:</strong> {getCountryLabel(this.state.selected.destination_country_code)}</p>
              <p><strong>Created:</strong> {formatDate(this.state.selected.created_date)}</p>
            </Col>
            <Col md={12}>
              <ButtonToolbar>
                {!this.state.isEditing ? (
                  <Button color="primary" onClick={() => this.setState({ isEditing: true })}>Edit</Button>
                ) : (
                  <React.Fragment>
                    <Button color="success" onClick={this.saveFulfilment}>Save Changes</Button>
                    <Button color="secondary" onClick={this.cancelEdit}>Cancel</Button>
                  </React.Fragment>
                )}
              </ButtonToolbar>
              {this.state.saveMessage ? <p>{this.state.saveMessage}</p> : null}
            </Col>
          </Row>

          <Row>
            {editableFields.map(field => (
              <Col md={3} key={field.key} style={{ marginBottom: 12 }}>
                <Label>{field.label}</Label>
                <Input
                  type={field.type}
                  step={field.step}
                  value={getFieldValue(this.state.selected, field)}
                  disabled={!this.state.isEditing}
                  onChange={event => this.updateSelectedField(field.key, event.target.value)}
                />
              </Col>
            ))}
          </Row>

          <hr />

          <Row>
            <Col md={8}>
              <Label check>
                <Input
                  type="checkbox"
                  checked={this.state.deleteEnabled}
                  onChange={event => this.setState({ deleteEnabled: event.target.checked })}
                />{' '}
                Enable delete for this fulfilment
              </Label>
              {this.state.deleteMessage ? <p>{this.state.deleteMessage}</p> : null}
            </Col>
            <Col md={4}>
              <Button
                color="danger"
                disabled={!this.state.deleteEnabled}
                onClick={this.deleteFulfilment}
              >
                Delete Fulfilment
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  };

  render() {
    const visibleIds = this.state.data.map(row => row.id);
    const allVisibleSelected = visibleIds.length > 0 &&
      visibleIds.every(id => this.state.selectedIds.includes(id));
    const columns = [
      {
        Header: () => {
          const checkbox = (
            <Input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={event => this.toggleVisibleSelections(event.target.checked)}
            />
          );
          return renderCenteredCheckbox(checkbox);
        },
        id: 'selected',
        sortable: false,
        filterable: false,
        width: 60,
        Cell: (row) => {
          const checkbox = (
            <Input
              type="checkbox"
              checked={this.state.selectedIds.includes(row.original.id)}
              onChange={event => this.toggleSelectedId(row.original.id, event.target.checked)}
              onClick={event => event.stopPropagation()}
            />
          );
          return renderCenteredCheckbox(checkbox);
        },
      },
      { Header: 'ID', accessor: 'id', width: 80 },
      { Header: 'Order Number', accessor: 'external_order_id' },
      { Header: 'Seller', accessor: 'seller_name' },
      { Header: 'Status', accessor: 'status' },
      { Header: 'Channel', accessor: 'sales_channel' },
      {
        Header: 'Shipped',
        accessor: 'shipped_date',
        Cell: row => formatDate(row.value),
      },
      { Header: 'Carrier', accessor: 'shipping_carrier' },
      { Header: 'Service', accessor: 'shipping_service' },
      { Header: 'Tracking', accessor: 'tracking_number' },
      {
        Header: 'Destination',
        accessor: 'destination_country_code',
        Cell: row => getCountryLabel(row.value),
      },
    ];

    return (
      <Container className="dashboard">
        <Row>
          <Col md={12}>
            <h3 className="page-title">Discogs Fulfilments</h3>
          </Col>
        </Row>

        <Card>
          <CardBody>
            <Row>
              <Col md={3}>
                <Label>Seller</Label>
                <Input
                  type="select"
                  value={this.state.sellerId}
                  onChange={event => this.updateFilter('sellerId', event.target.value)}
                >
                  <option value="">All Sellers</option>
                  {this.state.sellers.map(seller => (
                    <option key={seller.id} value={seller.id}>
                      {seller.name}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={3}>
                <Label>Shipped From</Label>
                <Input
                  type="date"
                  value={this.state.shippedDateFrom}
                  onChange={event => this.updateFilter('shippedDateFrom', event.target.value)}
                />
              </Col>
              <Col md={3}>
                <Label>Shipped To</Label>
                <Input
                  type="date"
                  value={this.state.shippedDateTo}
                  onChange={event => this.updateFilter('shippedDateTo', event.target.value)}
                />
              </Col>
              <Col md={3}>
                <Label check style={{ marginTop: 30 }}>
                  <Input
                    type="checkbox"
                    checked={this.state.shippedOnly}
                    onChange={event => this.updateFilter('shippedOnly', event.target.checked)}
                  />{' '}
                  Shipped Orders Only
                </Label>
              </Col>
            </Row>
            <ButtonToolbar style={{ marginTop: 12 }}>
              <Button color="primary" onClick={this.refreshTable}>Refresh</Button>
              <Button color="secondary" onClick={this.resetFilters}>Reset</Button>
              <Button color="success" disabled={this.state.exporting} onClick={this.exportCsv}>
                {this.state.exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </ButtonToolbar>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Row style={{ marginBottom: 12 }}>
              <Col md={8}>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={this.state.batchDeleteEnabled}
                    disabled={this.state.selectedIds.length === 0}
                    onChange={event => this.setState({ batchDeleteEnabled: event.target.checked })}
                  />{' '}
                  Enable batch delete ({this.state.selectedIds.length} selected)
                </Label>
                {this.state.deleteMessage ? <p>{this.state.deleteMessage}</p> : null}
              </Col>
              <Col md={4}>
                <Button
                  color="danger"
                  disabled={this.state.selectedIds.length === 0 || !this.state.batchDeleteEnabled}
                  onClick={this.batchDeleteFulfilments}
                >
                  Delete Selected
                </Button>
              </Col>
            </Row>
            <ReactTable
              manual
              filterable
              data={this.state.data}
              pages={this.state.pages}
              page={this.state.page}
              pageSize={this.state.pageSize}
              loading={this.state.loading}
              columns={columns}
              filtered={this.state.tableFilters}
              onFilteredChange={this.updateTableFilters}
              onPageChange={page => this.setState({ page })}
              onPageSizeChange={(pageSize, page) => this.setState({ pageSize, page })}
              onFetchData={this.fetchData}
              getTrProps={(state, rowInfo) => ({
                onClick: () => {
                  if (rowInfo && rowInfo.original) {
                    this.selectFulfilment(rowInfo.original);
                  }
                },
                style: {
                  cursor: 'pointer',
                  background: rowInfo && this.state.selected && rowInfo.original.id === this.state.selected.id
                    ? '#d7efff'
                    : '',
                },
              })}
            />
          </CardBody>
        </Card>

        {this.renderOrderItems()}

        {this.renderSelectedDetails()}
      </Container>
    );
  }
}

export default DiscogsFulfilmentsTable;
